import { RootState } from '@Redux/store'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import jwtDecode from 'jwt-decode'

import {
  changePassword,
  check,
  login,
  logoutPost,
  refreshToken,
  register,
  resetPass,
  resetPassLink,
} from '@App/api/auth'
import {
  changeNickname,
  createPaymentIntent,
  deposit,
  disable2FA,
  editAccount,
  enable2FA,
  moveAccountMoney,
  withdrawal,
  getUserRealityCheck,
  acceptNewPolicy,
  serverValidate,
  getUserLimits,
  setUserLimits,
  editRealityChecksPeriod,
  editUserSettings,
  setSelfExclusion,
  userCloseAccount,
  getUserDocuments,
  getUserDocumentFiles,
  getUserImageUrlRequest,
  uploadUserImage,
  uploadFilesComplete,
  deleteUserUploadFile,
  checkUserAnswers,
  editTimeoutsProblemGambler,
  createAccount,
  accountDeposit,
} from '@App/api/user'
import authCookieManager from '@App/cookies/auth_cookie_manager'
import localStorageManager from '@App/cookies/local_storage_manager'
import type { IUser } from '@App/redux/interfaces'
import { authToFrontServiceCommon } from '@App/redux/slices/sockets/CommonConnectionSlice'
import { generateRequestKey } from '@App/sockets/helpers'
import UserServiceConnection from '@App/sockets/user_service_connection'

import { MODAL_TYPES } from '@Redux/slices/modals/enums'
import { setModal } from '@Redux/slices/modals/ModalSlice'
import { closeFrontServiceSocket } from '@Redux/slices/sockets/FrontServiceConnectionSlice'
import {
  closeProxyServiceSocket,
  getNonExistWallets,
  setTokenProxyServiceConnection,
  setUserWalletConnection,
  setUserOrdersConnection,
  updateTokenProxyServiceConnection,
  setWatchlistsConnection,
  setUserPriceAlertsConnection,
} from '@Redux/slices/sockets/ProxyServiceConnectionSlice'
import { closeUserServiceSocket, subscribeToUserService } from '@Redux/slices/sockets/UserServiceConnectionSlice'

import {
  ILoginRequest,
  User,
  IWithdrawalRequest,
  IJwtDecodeData,
  IResetDataRequest,
  IChangePasswordDataRequest,
  Wallet,
  IAccountDepositRequest,
} from '@Interfaces/CommonInterfaces'

import { addErrorNotify, addSuccessNotify, createNotificationObject } from '../notifications/NotificationSlice'

import {
  ICreateAccountData,
  IEditAccountData,
  ILoginData,
  ILoginDataError,
  IMoveAccountMoneyData,
  IRegisterData,
  IRegisterResponse,
} from './types'

import type { PayloadAction } from '@reduxjs/toolkit'
import { resetToDefaults } from '../watchlist/slice'

import { $host } from '@App/api'

const initialState: IUser = {
  login: false,
  user_id: 0,
  username: localStorageManager.get('username'),
  user_info: {
    id: '',
    username: '',
    nickname: '',
    mobile_phone: '',
    email: '',
    permissions: [],
    ip: localStorageManager.get('ip') || '',
    registration_ip: '',
    language: '',
    country: '',
    front_id: '',
    test: false,
    update_time: '',
    last_login_time: '',
    last_action_time: '',
    registration_time: '',
    role: '',
    two_fa_enabled: false,
    wallet_ids: [],
    groups: {},
    is_readonly_reality_check: false,
  },
  chartSettings: {
    chartInterval: localStorageManager.get('tradingview.chart.lastUsedTimeBasedResolution') || '1',
    chartType: localStorageManager.get('tradingview.chart.lastUsedStyle') || '1',
  },
  wallets: [],
  player_limits: [],
  ip: localStorageManager.get('ip') || '',
  user_agent: localStorageManager.get('user_agent') || '',
  user_session: localStorageManager.get('user_session') || '',
  is_loading: false,
  is_success: false,
  check_load: false,
  theme: localStorageManager.get('theme') || 'dark',
  curr_wallet: localStorageManager.get('currWallet') ? new Wallet({ id: Number(localStorageManager.get('wallet') ?? 0) }) : null,
  favorite_ticker_id: JSON.parse(localStorageManager.get('favoriteItems') ?? '[]'),
  required_two_fa: false,
  realityCheckData: null,
  realityCheckLoading: false,
  is_readonly_reality_check: false,
  termsAcceptedAt: null as string | null,
  session_timeout: null,
  exclude_period: null,
  exclude_kyc: false,
  is_readonly: false,
  reality_check_period: null,
  bot: false,
  documentRequests: null,
  kyc_documents_status_count: null,
  documentFiles: null as null | Record<string, any[]>,
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLogin: (state, action: PayloadAction<string>) => {
      state.login = true
      state.username = action.payload
    },
    setLoadingLogin: (state) => {
      state.is_loading = true
    },
    clientLogout: (state) => {
      state.login = false
      state.username = ''
      state.user_info = null
    },

    checkUser: (state) => {
      state.login = true
    },
    changeTheme: (state) => {
      state.theme = state.theme === 'dark' ? 'light' : 'dark'
    },
    setFavoriteTickerId: (state) => {
      state.favorite_ticker_id = JSON.parse(localStorageManager.get('favoriteItems') ?? '[]')
    },
    clearVerificationState: (state) => {
      state.required_two_fa = false
      window.history.replaceState({}, document.title)
    },
    startCheckLoad: (state) => {
      state.check_load = true
    },
    finishCheckLoad: (state) => {
      state.check_load = false
    },
    removeWallet: (state, action: PayloadAction<number>) => {
      const walletId = action.payload

      state.wallets = state.wallets.filter(w => Number(w.id) !== walletId)

      if (Number(state.curr_wallet?.id) === walletId) {
        state.curr_wallet = null
      }
    },
    updateRealityCheckPeriod: (state, action: PayloadAction<number>) => {
      state.reality_check_period = action.payload
    },
    updateIsReadonlyRealityCheckPeriod: (state, action: PayloadAction<boolean>) => {
      state.is_readonly_reality_check = action.payload
    },
    updateSessionTimeout: (state, action: PayloadAction<number>) => {
      state.session_timeout = Number(action.payload)
    },
    updateExcludePeriod: (state, action: PayloadAction<number>) => {
      state.exclude_period = Number(action.payload)
    },
    updateExcludeKYC: (state, action: PayloadAction<boolean>) => {
      state.exclude_kyc = action.payload
    },
    updateReadonly: (state, action: PayloadAction<boolean>) => {
      state.is_readonly = action.payload
    },
    updateIsUserBot: (state, action: PayloadAction<boolean>) => {
      state.bot = action.payload
    },
    updateKycDocumentsCount: (state, action: PayloadAction<any>) => {
      state.kyc_documents_status_count = action.payload
    },
    updateUserCountry: (state, action: PayloadAction<string>) => {
      state.user_info!.country = action.payload
    },
    setChartInterval: (state, action: PayloadAction<string>) => {
      state.chartSettings.chartInterval = action.payload
      localStorageManager.set('tradingview.chart.lastUsedTimeBasedResolution', action.payload)
    },
    setChartType: (state, action: PayloadAction<string>) => {
      state.chartSettings.chartType = action.payload
      localStorageManager.set('tradingview.chart.lastUsedStyle', action.payload.toString())
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.fulfilled, (state, action: any) => {
        state.login = true
        state.username = action.payload.username
        state.ip = action.payload.ip
        state.user_id = action.payload.id
        state.is_loading = false
        state.user_agent = action.payload.user_agent
        state.user_session = action.payload.user_session

        UserServiceConnection.sendPlatformUser({
          session: state.user_session,
          ip: state.ip,
          user_agent: state.user_agent,
        })
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.is_loading = false
        if (action.payload === 'TWO_FA_REQUIRED') {
          state.required_two_fa = true
        }
      })
      .addCase(setCurrWallet.fulfilled, (state, action) => {
        const wallet = state?.wallets?.find((wallet) => wallet.id === action.payload.walletId) ?? null

        if (wallet) {
          state.curr_wallet = wallet
          localStorageManager.set('wallet', wallet.id)
        }
      })
      .addCase(setUserInfo.fulfilled, (state, action: any) => {
        if (action.payload.user_info) {
          if (!state.login) {
            state.login = true
          }
          state.username = action.payload.user_info.username
          state.user_id = action.payload.user_info.id
          state.user_info = action.payload.user_info
        }
      })
      .addCase(setWallet.fulfilled, (state, action: any) => {
        if (action.payload.wallet) {
          const wallet = new Wallet(action.payload.wallet)
          const exist_index = state.wallets.findIndex((w) => w.id === wallet.id)

          if (exist_index >= 0) {
            state.wallets[exist_index] = wallet
          } else {
            state.wallets.push(wallet)
          }

          state.wallets.sort((a, b) => {
            const dateA = new Date(a.created_at).getTime()
            const dateB = new Date(b.created_at).getTime()

            if (dateB !== dateA) {
              return dateB - dateA
            }

            return Number(a.id) - Number(b.id)
          })

          if (state.user_info?.wallet_ids?.length && state.wallets.length === state.user_info.wallet_ids.length
            && (!state.curr_wallet?.id || state.curr_wallet.id === wallet.id)) {
            state.curr_wallet = state.wallets[0]
            localStorageManager.set('wallet', state.curr_wallet.id)
          }
        }
      })
      .addCase(asyncLogout.fulfilled, (state) => {
        localStorageManager.clear()
        return { ...initialState, theme: state.theme }
      })

      .addCase(asyncResetPassLink.pending, (state) => {
        state.is_loading = true
      })
      .addCase(asyncResetPassLink.fulfilled, (state) => {
        state.is_loading = false
      })
      .addCase(asyncResetPassLink.rejected, (state) => {
        state.is_loading = false
      })

      .addCase(asyncResetPass.pending, (state) => {
        state.is_loading = true
        state.is_success = false
      })
      .addCase(asyncResetPass.fulfilled, (state) => {
        state.is_loading = false
        state.is_success = true
      })
      .addCase(asyncResetPass.rejected, (state) => {
        state.is_loading = false
      })

      .addCase(asyncEnable2FA.pending, (state) => {
        state.is_loading = true
      })
      .addCase(asyncEnable2FA.fulfilled, (state) => {
        state.is_loading = false

        if (state.user_info) {
          state.user_info.two_fa_enabled = true
        }
      })
      .addCase(asyncEnable2FA.rejected, (state) => {
        state.is_loading = false
      })

      .addCase(asyncDisable2FA.pending, (state) => {
        state.is_loading = true
      })

      .addCase(asyncDisable2FA.fulfilled, (state) => {
        state.is_loading = false
        state.required_two_fa = false

        if (state.user_info) {
          state.user_info.two_fa_enabled = false
        }
      })
      .addCase(asyncDisable2FA.rejected, (state) => {
        state.is_loading = false
      })

      .addCase(changeNicknameAsync.fulfilled, (state, action) => {
        if (action.payload.nickname) {
          if (state.user_info) {
            state.user_info.nickname = action.payload.nickname
          }
        }
      })

      .addCase(asyncGetPlaySafeLimits.pending, (state) => {
        state.is_loading = true
      })
      .addCase(asyncGetPlaySafeLimits.fulfilled, (state, action) => {
        state.player_limits = action.payload.data
      })
      .addCase(asyncGetPlaySafeLimits.rejected, (state) => {
        state.is_loading = false
      })

      .addCase(asyncGetUserRealityCheck.pending, (state) => {
        state.realityCheckLoading = true
      })
      .addCase(asyncGetUserRealityCheck.fulfilled, (state, action) => {
        state.realityCheckLoading = false
        state.realityCheckData = action.payload
      })
      .addCase(asyncGetUserRealityCheck.rejected, (state) => {
        state.realityCheckLoading = false
      })
  },
})

let timeout_update_token: null | number = null

export const refreshTokenAsync = createAsyncThunk('user/refresh', async (_, { dispatch }) => {
  try {
    const result = await refreshToken()
    if (result) {
      dispatch(createNotificationObject({ message: 'Refresh token', status: 'info', id: generateRequestKey('global') }))
      dispatch(setUserExpTimeForToken())
      dispatch(updateTokenProxyServiceConnection(result.data.access_token))
      dispatch(authToFrontServiceCommon(result.data.access_token))
    } else {
      dispatch(asyncLogout())
    }
  } catch (error) {
    console.error('Refresh token failed:', error)
    dispatch(asyncLogout())
  }
})

export const loginAsync = createAsyncThunk('user/login', async (data: ILoginRequest, { dispatch, rejectWithValue }) => {
  authCookieManager.clearTokenCookie()
  const result: ILoginData | ILoginDataError = await login(data.username, data.password, data.verification_code)
  if ('username' in result) {
    const token = result.access_token ?? ''

    dispatch(setTokenProxyServiceConnection(token))
    dispatch(setWatchlistsConnection(token))
    dispatch(authToFrontServiceCommon(token))
    dispatch(setUserExpTimeForToken())
    dispatch(
      createNotificationObject({ message: 'Success login', id: generateRequestKey('global'), status: 'success' }),
    )

    const realityCheck = await dispatch(asyncGetUserRealityCheck()).unwrap()

    dispatch(
      setModal({
        modalType: MODAL_TYPES.CHECK_MONTH_STATS_AFTER_LOGIN_MODAL,
      }),
    )

    return result
  } else {
    if ('data' in result && result.data?.error_code === 'TWO_FA_REQUIRED') {
      return rejectWithValue(result.data?.error_code)
    } else {
      dispatch(
        createNotificationObject({
          message: result.message ? result.message : 'Try login later',
          id: generateRequestKey('global'),
          status: 'error',
        }),
      )
    }

    return rejectWithValue(result)
  }
})

export const registerAsync = createAsyncThunk(
  'user/register',
  async (payload: IRegisterData, { dispatch, rejectWithValue }) => {
    const res: IRegisterResponse | string = await register(payload)

    if (typeof res === 'string') {
      dispatch(
        createNotificationObject({
          message: res ?? 'Try login later',
          id: generateRequestKey('global'),
          status: 'error',
        }),
      )
      return rejectWithValue('')
    }
    dispatch(loginAsync({ username: payload.username, password: payload.password }))
  },
)

export const checkAsync = createAsyncThunk('user/check', async (_, { dispatch, getState }) => {
  const token = authCookieManager.getAccessToken()

  if (!token) {
    dispatch(finishCheckLoad())
    throw new Error('Invalid token')
  }

  dispatch(startCheckLoad())

  try {
    dispatch(setTokenProxyServiceConnection(token))
    dispatch(setWatchlistsConnection(token))
    dispatch(authToFrontServiceCommon(token))
    dispatch(subscribeToUserService())
    dispatch(setUserExpTimeForToken())
    const state = getState() as RootState
    UserServiceConnection.sendPlatformUser({
      session: state.user.user_session,
      ip: state.user.ip,
      user_agent: navigator.userAgent,
    })
    dispatch(checkUser())

    return token
  } finally {
    setTimeout(() => {
      dispatch(finishCheckLoad())
    }, 100)
  }
})

export const changeNicknameAsync = createAsyncThunk('api/clients/update', async (params: { nickname: string }) => {
  await changeNickname(params.nickname)

  return { nickname: params.nickname }
})

export const setWallet = createAsyncThunk('user/setUserWallet', async (wallet: Wallet, { dispatch }) => {
  const curWall = localStorageManager.get('wallet')

  if (!curWall || curWall == wallet.id) {
    dispatch(setUserOrdersConnection({ accountId: Number(wallet.id) }))
  }

  return { wallet }
})

export const setCurrWallet = createAsyncThunk('user/setCurrentWallet', async (walletId: string, { dispatch }) => {
   if (walletId) {
     dispatch(setUserOrdersConnection({ accountId: Number(walletId) }))
   }

  return { walletId }
})

export const handleWalletRemoval = createAsyncThunk<
  void,
  number,
  { state: RootState }
>(
  'user/handleWalletRemoval',
  async (walletId, { dispatch, getState }) => {
    const state = getState()
    const wasCurrent = Number(state.user.curr_wallet?.id) === walletId

    dispatch(removeWallet(walletId))

    if (wasCurrent) {
      const wallets = getState().user.wallets

      if (wallets.length > 0) {
        dispatch(setCurrWallet(wallets[0].id))
      }
    }
  },
)

export const setUserInfo = createAsyncThunk('user/setUserInfo', async (user_info: User, { dispatch }) => {
  if (user_info?.wallet_ids?.length) {
    const new_wallet_ids = getNonExistWallets(user_info?.wallet_ids)
    !!new_wallet_ids?.length && new_wallet_ids?.map((wallet_id) => dispatch(setUserWalletConnection(wallet_id)))
  }
  dispatch(setUserPriceAlertsConnection({ userId: Number(user_info.id) }))
  return { user_info }
})

export const setUserExpTimeForToken = createAsyncThunk('user/setUserExpTimeForToken', async (_, { dispatch }) => {
  const token = authCookieManager.getAccessToken()

  if (!token) {
    dispatch(asyncLogout())
    return
  }

  try {
    const data: { exp: number } = jwtDecode(token)
    const timeLeft = data.exp * 1000 - new Date().getTime() - 60 * 1000

    if (timeout_update_token !== null) {
      clearTimeout(timeout_update_token)
      timeout_update_token = null
    }

    if (timeLeft <= 0) {
      dispatch(refreshTokenAsync())
    } else {
      timeout_update_token = window.setTimeout(() => {
        dispatch(refreshTokenAsync())
      }, timeLeft)
    }
  } catch (error) {
    console.error('Invalid token', error)
    dispatch(asyncLogout())
  }
})

export const asyncLogout = createAsyncThunk('user/asyncLogout', async (_, { dispatch }) => {
  try {
    await logoutPost()
  } catch (error) {
    console.error('Logout API failed', error)
  }

  authCookieManager.clearTokenCookie()
  localStorageManager.clear()
  dispatch(closeFrontServiceSocket())
  dispatch(closeProxyServiceSocket())
  dispatch(closeUserServiceSocket())
  dispatch(resetToDefaults())
  if (timeout_update_token) {
    clearTimeout(timeout_update_token)
    timeout_update_token = null
  }

  return _
})

export const depositAsync = createAsyncThunk('user/deposit', async (data: any) => {
  const result: any = await deposit(data)
  return result
})

export const accountDepositAsync = createAsyncThunk('account/deposit', async (data: IAccountDepositRequest) => {
  const result: any = await accountDeposit(data)
  return result
})

export const createPaymentIntentAsync = createAsyncThunk('user/createIntent', async (data: any) => {
  const result: any = await createPaymentIntent(data)
  return result
})

export const withdrawalAsync = createAsyncThunk('user/withdrawal', async (data: IWithdrawalRequest) => {
  const result: any = await withdrawal(data.currency, data.value)
  return result
})

export const asyncResetPassLink = createAsyncThunk('user/resetPassLink', async (email: string, { dispatch }) => {
  const response: any = await resetPassLink(email)
  response.status === false
    ? dispatch(addErrorNotify({ message: response.data.message }))
    : dispatch(
        addSuccessNotify({
          message: 'Link to reset password has been successfully sent. Please, check your email to continue',
        }),
      )
})

export const asyncResetPass = createAsyncThunk(
  'user/resetPass',
  async (data: IResetDataRequest, { dispatch, rejectWithValue }) => {
    const err: any = await resetPass(data)
    err
      ? dispatch(addErrorNotify({ message: err }))
      : dispatch(
          addSuccessNotify({
            message: 'Your password has been successfully changed',
          }),
        )

    if (err) return rejectWithValue('')
  },
)

export const asyncChangePass = createAsyncThunk(
  'user/changePass',
  async (data: IChangePasswordDataRequest, { dispatch, rejectWithValue }) => {
    const err: any = await changePassword(data)
    err
      ? dispatch(addErrorNotify({ message: err }))
      : dispatch(
          addSuccessNotify({
            message: 'Your password has been successfully changed',
          }),
        )

    if (err) return rejectWithValue('')
  },
)

export const asyncEnable2FA = createAsyncThunk(
  'user/two-factor/enable',
  async (params: { code: string; secretKey: string }, { dispatch, rejectWithValue }) => {
    const err: any = await enable2FA(params.code, params.secretKey)
    if (err) {
      dispatch(addErrorNotify({ message: err }))
      return rejectWithValue('')
    }
  },
)

export const asyncDisable2FA = createAsyncThunk(
  'user/two-factor/disable',
  async (params: { code: string }, { dispatch, rejectWithValue }) => {
    const err: any = await disable2FA(params.code)
    if (err) {
      dispatch(addErrorNotify({ message: err }))
      return rejectWithValue('')
    }
  },
)

export const asyncEditAccount = createAsyncThunk(
  'account/edit',
  async (params: IEditAccountData, { dispatch, rejectWithValue }) => {
    const resp: { status: number; message: string } = await editAccount(params)

    !resp?.status && dispatch(addErrorNotify({ message: resp?.message }))

    if (resp?.status) return rejectWithValue('')
  },
)

export const asyncCreateAccount = createAsyncThunk(
  'account/create',
  async (params: ICreateAccountData, { dispatch, rejectWithValue }) => {
    const resp: { status: number; message: string } = await createAccount(params)

    !resp?.status && dispatch(addErrorNotify({ message: resp?.message }))
    if ('data' in resp) {
      if ('balance' in resp.data) {
        const data = resp.data as any as { id: number; balance: { currency: string }; name: string }
        dispatch(
          setModal({
            modalType: MODAL_TYPES.ACCOUNT_CREATED_MODAL,
            data: { id: data.id, currency: data.balance.currency, name: data.name },
          }),
        )
      }
    }
  },
)

export const asyncMoveAccountMoney = createAsyncThunk(
  'account/move-money',
  async (params: IMoveAccountMoneyData, { dispatch, rejectWithValue }) => {
    const resp: { status: number; message: string } = await moveAccountMoney(params)

    !resp?.status && dispatch(addErrorNotify({ message: resp?.message }))

    if (resp?.status) return rejectWithValue('')
  },
)

export const asyncGetUserRealityCheck = createAsyncThunk('user/getRealityCheck', async () => {
  const resp: any = await getUserRealityCheck()

  return resp
})

export const asyncAcceptNewPolicy = createAsyncThunk('user/acceptNewPolicy', async (params: any) => {
  const resp: any = await acceptNewPolicy(params)

  return resp
})

export const asyncValidate = createAsyncThunk('user/validate', async (params: any) => {
  const resp: any = await serverValidate(params)

  return resp
})

export const asyncGetPlaySafeLimits = createAsyncThunk('user/getPlaySafeLimits', async () => {
  const resp: any = await getUserLimits()

  return resp
})

export const asyncSetPlaySafeLimits = createAsyncThunk('user/setPlaySafeLimits', async (params: any) => {
  const resp: any = await setUserLimits(params)

  return resp
})

export const asyncEditRealityChecksPeriod = createAsyncThunk('user/editRealityChecksPeriod', async (params: any) => {
  const resp: any = await editRealityChecksPeriod(params)

  return resp
})

export const asyncEditUserSettings = createAsyncThunk('user/editUserSettings', async (params: any) => {
  const resp: any = await editUserSettings(params)

  return resp
})

export const asyncSetSelfExclusion = createAsyncThunk('user/setSelfExclusion', async (params: any) => {
  const resp: any = await setSelfExclusion(params)

  return resp
})

export const asyncUserCloseAccount = createAsyncThunk('user/userCloseAccount', async (params: any) => {
  const resp: any = await userCloseAccount(params)

  return resp
})

export const asyncDeleteUserImage = createAsyncThunk('user/userCloseAccount', async (params: any) => {
  const resp: any = await deleteUserUploadFile(params)

  return resp
})

export const asyncUploadUserImage = createAsyncThunk('user/uploadUserImage', async (params: any) => {
  const resp: any = await uploadUserImage(params)

  return resp
})

export const isLogin = (state: RootState) => (state.user ? state.user.login : false)
export const isPending = (state: RootState) => (state.user ? state.user.is_loading : false)
export const isSuccess = (state: RootState) => (state.user ? state.user.is_success : false)
export const requiredTwoFA = (state: RootState) => (state.user ? state.user.required_two_fa : false)
export const userState = (state: RootState) => state.user
export const userWalletsFiat = (state: RootState) => {
  const wallets = state.user?.wallets ? state.user.wallets : []

  return wallets.filter((wallet) => wallet.type === 'fiat')
}

export const {
  setLogin,
  clientLogout,
  checkUser,
  setLoadingLogin,
  changeTheme,
  setFavoriteTickerId,
  clearVerificationState,
  startCheckLoad,
  finishCheckLoad,
  removeWallet,
  updateRealityCheckPeriod,
  updateSessionTimeout,
  updateExcludePeriod,
  updateExcludeKYC,
  updateReadonly,
  updateIsUserBot,
  updateIsReadonlyRealityCheckPeriod,
  updateKycDocumentsCount,
  updateUserCountry,
  setChartInterval,
  setChartType,
} = userSlice.actions
export default userSlice.reducer
